/**
 * Sidebar tree mirrored from WPF `NavigationSidebarViewModel` (top-level order and nesting).
 * Labels resolve from `NavigationCommandRegistry` via `wpfNavLabels.json`.
 * Icons match `ColorIcon_Sidebar_*` → `/icons/sidebar/*.png`.
 *
 * User Dashboards: only static entries (My Dashboards / Create Dashboard). WPF also lists
 * custom dashboards from runtime — omitted until a web API exists.
 */
import { isRealAssetFromAssetPath } from '../features/realAssets/realAssetRoutes';
import type { NavGroup, NavLeaf, NavNode, NavPlaceholderLeaf } from './navTypes';
import { pathMatchesTo, splitNavTo } from './navTypes';
import { labelForWpfKey, pathForWpfKey } from './navPaths';

function defaultLeafMatch(to: string): (pathname: string, search?: string) => boolean {
  return (pathname, search = '') => {
    const target = splitNavTo(to);
    if (target.pathname.startsWith('/p/')) {
      return pathname === target.pathname && pathMatchesTo(pathname, to, search);
    }
    return pathMatchesTo(pathname, to, search);
  };
}

function leaf(
  id: string,
  wpfKey: string,
  iconFile: string,
  match?: (pathname: string, search?: string) => boolean
): NavLeaf {
  const to = pathForWpfKey(wpfKey);
  return {
    kind: 'leaf',
    id,
    label: labelForWpfKey(wpfKey),
    to,
    wpfKey,
    iconFile,
    match: match ?? defaultLeafMatch(to),
  };
}

/** WPF Home / Home (Advanced) / Command Explorer — no Feature, `PermissionLevel.None`. */
function shellLeaf(
  id: string,
  label: string,
  to: string,
  iconFile: string,
  match?: (pathname: string, search?: string) => boolean
): NavLeaf {
  return {
    kind: 'leaf',
    id,
    label,
    to,
    iconFile,
    shell: true,
    match: match ?? defaultLeafMatch(to),
  };
}

function group(id: string, label: string, iconFile: string, children: NavNode[]): NavGroup {
  return { kind: 'group', id, label, iconFile, children };
}

function ph(id: string, label: string, iconFile?: string): NavPlaceholderLeaf {
  return { kind: 'placeholder', id, label, iconFile };
}

/**
 * The instrument search leaves both point at `/instruments`, so a plain prefix match would also
 * claim the `/instruments/new/*` define screens. Those are sibling leaves under Instruments,
 * not children of search.
 */
function isInstrumentSearchPath(pathname: string): boolean {
  if (pathname === '/instruments') return true;
  if (pathname.startsWith('/instruments/new')) return false;
  return /^\/instruments\/[^/]+$/.test(pathname);
}

/** Search Clients — not create, onboard, or workbench routes. */
function isClientListPath(pathname: string): boolean {
  if (pathname === '/clients') return true;
  return /^\/clients\/[0-9a-fA-F-]{36}$/.test(pathname);
}

export const navigationTree: NavNode[] = [
  group('clients', 'Clients', '064-group.png', [
    leaf('cl-client-list', 'ClientList', '062-magnifying-glass.png', isClientListPath),
    leaf('cl-create-client', 'CreateClient', '049-user-1.png'),
    leaf('cl-onboard', 'OnboardClient', '041-promotion.png'),
    leaf('cl-kyc', 'KycDocumentVerification', '090-cheque.png'),
    leaf('cl-screening-q', 'ComplianceScreeningQueue', '059-shield-3.png'),
    leaf('cl-approval-q', 'OnboardingReviewQueue', '067-prize.png'),
    leaf('cl-risk-templates', 'RiskQuestionnaireTemplates', '097-analytics.png'),
    leaf('cl-onb-dash', 'OnboardingDashboard', '097-analytics.png'),
    ph('cl-ph-households', 'Households'),
    ph('cl-ph-mandates', 'Mandates'),
    ph('cl-ph-fee-arr', 'Fee Arrangements'),
    ph('cl-ph-rep-groups', 'Reporting Groups'),
    ph('cl-ph-agreements', 'Client Agreements'),
  ]),

  group('portfolio-management', 'Portfolio Management', '002-briefcase-1.png', [
    leaf(
      'pm-search-portfolios',
      'SearchPortfolios',
      '062-magnifying-glass.png',
      (p) =>
        p === '/portfolios' ||
        /^\/portfolios\/[^/]+$/.test(p) ||
        /^\/portfolios\/[^/]+\/wealth$/.test(p)
    ),
    leaf('pm-create-portfolio', 'CreatePortfolio', '033-briefcase.png'),
    leaf(
      'pm-search-accounts',
      'SearchAccounts',
      '062-magnifying-glass.png',
      (p) => p === '/accounts' || /^\/accounts\/[^/]+$/.test(p)
    ),
    leaf('pm-open-account', 'OpenAccount', '015-accounts.png'),
    leaf(
      'pm-search-custodians',
      'SearchCustodians',
      '062-magnifying-glass.png',
      (p) => p === '/custodians' || /^\/custodians\/[^/]+$/.test(p)
    ),
    leaf('pm-create-custodian', 'CreateCustodian', '022-bank.png'),
  ]),

  group('instruments', 'Instruments', '050-line-chart.png', [
    leaf('ins-search', 'SearchInstrument', '062-magnifying-glass.png', isInstrumentSearchPath),
    leaf('ins-search-all', 'SearchAllAssets', '062-magnifying-glass.png', isInstrumentSearchPath),
    leaf('ins-def-equity', 'DefineEquity', '028-bar-chart-2.png'),
    leaf('ins-def-frb', 'DefineFixedRateInstrument', '026-contract-1.png'),
    leaf('ins-def-zcb', 'DefineZeroCouponBond', '026-contract-1.png'),
    leaf('ins-def-flb', 'DefineFloatingRateBond', '026-contract-1.png'),
    leaf('ins-def-td', 'DefineTimeDeposit', '056-dollar.png'),
    leaf('ins-def-loan', 'DefineLoan', '056-dollar.png'),
    leaf('ins-def-fund', 'DefineFund', '024-analytics-2.png'),
    leaf('ins-def-comm', 'DefineCommodity', '032-earth-grid.png'),
    leaf('ins-def-ccy', 'DefineCurrency', '052-euro-1.png'),
    leaf('ins-def-fx-pair', 'DefineFxCurrencyPair', '055-euro.png'),
    leaf('ins-def-idx-fut', 'DefineIndexFutures', '050-line-chart.png'),
    leaf('ins-def-stk-fut', 'DefineStockFutures', '050-line-chart.png'),
    leaf('ins-def-ccy-fut', 'DefineCurrencyFutures', '050-line-chart.png'),
    leaf('ins-def-cmd-fut', 'DefineCommodityFutures', '050-line-chart.png'),
    leaf('ins-def-idx-opt', 'DefineIndexOption', '100-diagram.png'),
    leaf('ins-def-stk-opt', 'DefineStockOption', '100-diagram.png'),
    leaf('ins-def-opt-fut', 'DefineOptionOnFutures', '028-bar-chart-2.png'),
    leaf('ins-def-fxo', 'DefineFxOption', '055-euro.png'),
    leaf('ins-def-real-asset', 'DefineRealAsset', '067-prize.png'),
    leaf('ins-def-index', 'DefineIndex', '028-bar-chart-2.png'),
    leaf('ins-classification', 'ClassificationSchemes', '082-hierarchical-structure-1.png'),
  ]),

  group('market-data', 'Market Data', '081-bar-chart.png', [
    leaf('md-price-browser', 'MarketPricesBrowser', '062-magnifying-glass.png'),
    leaf('md-fx-browser', 'ForexRatesBrowser', '055-euro.png'),
  ]),

  group('entities', 'Entities', '032-earth-grid.png', [
    leaf('ent-search-exch', 'SearchExchanges', '062-magnifying-glass.png'),
    leaf('ent-create-exch', 'CreateExchange', '022-bank.png'),
  ]),

  group('deal-capture', 'Deal Capture', '090-cheque.png', [
    leaf(
      'dc-search-deals',
      'SearchDeals',
      '062-magnifying-glass.png',
      (p) => p === '/deals/search' || /^\/deals\/[0-9a-fA-F-]{36}$/.test(p)
    ),
    leaf('dc-ai', 'AiDealCapture', '003-puzzle.png', (p) => p === '/deals/capture/ai'),
    leaf('dc-buy', 'CaptureBuy', '039-money.png'),
    leaf('dc-sell', 'CaptureSell', '039-money-revesed.PNG'),
    leaf('dc-fx-spot', 'CaptureFxSpot', '056-dollar.png'),
    leaf('dc-fx-fwd', 'CaptureFxForward', '052-euro-1.png'),
    leaf('dc-buy-otc', 'CaptureBuyOtcOption', '100-diagram.png'),
    leaf('dc-sell-otc', 'CaptureSellOtcOption', '050-line-chart.png'),
    leaf('dc-place-td', 'PlaceTermDeposit', '039-money.png'),
    leaf('dc-break-td', 'BreakTermDeposit', '039-money-revesed.PNG'),
    leaf('dc-drawdown', 'DrawdownLoan', '039-money-revesed.PNG'),
    leaf('dc-repay', 'RepayLoanEarly', '039-money.png'),
    leaf('dc-gift', 'CaptureGift', '067-prize.png'),
    leaf('dc-inherit', 'CaptureInheritance', '065-prize-1.png'),
    leaf('dc-dep', 'CaptureDeposit', '039-money.png'),
    leaf('dc-wd', 'CaptureWithdraw', '039-money-revesed.PNG'),
    leaf('dc-recv-long', 'ReceiveLong', '039-money.png'),
    leaf('dc-recv-sh', 'ReceiveShort', '040-fallen.png'),
    leaf('dc-del-long', 'DeliverLong', '037-money-1.png'),
    leaf('dc-del-sh', 'DeliverShort', '033-briefcase.png'),
    leaf('dc-income', 'CaptureIncome', '039-money.png'),
    leaf('dc-expense', 'CaptureExpense', '037-money-1.png'),
  ]),

  group('private-equity', 'Private Equity', '021-safebox.png', [
    leaf('pe-search', 'SearchParticipations', '062-magnifying-glass.png'),
    leaf('pe-define', 'DefinePrivateEquity', '029-contract.png'),
    leaf('pe-capital', 'CapturePrivateEquityActivity', '006-wallet.png'),
    leaf('pe-receive', 'ReceivePrivateEquity', '039-money.png'),
  ]),

  group('corporate-actions', 'Corporate Actions', '014-bell.png', [
    leaf('ca-search', 'SearchCorporateActions', '062-magnifying-glass.png'),
    leaf('ca-cash-div', 'CaptureCashDividend', '006-wallet.png'),
    leaf('ca-stock-div', 'CaptureStockDividend', '050-line-chart.png'),
    leaf('ca-roc', 'CaptureReturnOfCapital', '039-money.png'),
    leaf('ca-cgd', 'CaptureCapitalGainsDistribution', '037-money-1.png'),
    leaf('ca-split', 'CaptureStockSplit', '003-puzzle.png'),
    leaf('ca-rev-split', 'CaptureReverseStockSplit', '082-hierarchical-structure-1.png'),
  ]),

  group('compliance', 'Compliance', '099-shield.png', [
    leaf('cmp-dash', 'ComplianceDashboard', '097-analytics.png'),
    leaf('cmp-rules-dash', 'ComplianceRulesDashboard', '098-shield-1.png'),
    leaf('cmp-create-rule', 'CreateComplianceRule', '098-shield-1.png'),
    leaf('cmp-suggest', 'SuggestComplianceRules', '003-puzzle.png'),
    leaf('cmp-create-pol', 'CreateCompliancePolicy', '099-shield.png'),
  ]),

  group('fees', 'Fees', '052-euro-1.png', [
    leaf('fe-browse', 'BrowseFeeRules', '098-shield-1.png'),
    leaf('fe-create', 'CreateFeeRule', '098-shield-1.png'),
    leaf('fe-dev', 'FeeDeviation', '013-calculator.png'),
    leaf('fe-reclaim', 'ReclaimableCharges', '006-wallet.png'),
    leaf('fe-retro', 'Retrocession', '050-line-chart.png'),
  ]),

  // Product dashboards live in Report Explorer (catalog Kind=Dashboard), not a
  // separate Reports → Dashboards sidebar tree — matches WPF NavigationSidebarViewModel.
  group('reports', 'Reports', '097-analytics.png', [
    leaf('rp-explorer', 'ReportExplorer', '062-magnifying-glass.png', (p) => p === '/reports/explorer'),
    leaf('rp-holdings', 'Holdings', '002-briefcase-1.png'),
    leaf('rp-bond-holdings', 'BondHoldings', '026-contract-1.png'),
    leaf('rp-term-deposits', 'TermDeposits', '021-safebox.png'),
    leaf('rp-loans', 'Loans', '056-dollar.png'),
    leaf(
      'rp-real-assets-dash',
      'RealAssetsDashboard',
      '067-prize.png',
      (p) => p === '/reports/dashboards/real-assets' || isRealAssetFromAssetPath(p)
    ),
    leaf('rp-events', 'UpcomingEvents', '057-wall-clock.png'),
    leaf('rp-recon-workbench', 'ReconciliationWorkbench', '098-shield-1.png'),
    leaf('rp-asset-recon', 'AssetReconciliation', '093-hierarchical-structure.png'),
    leaf('rp-bond-recon', 'BondReconciliation', '026-contract-1.png'),
    leaf('rp-fe-dev', 'FeeDeviation', '013-calculator.png'),
    leaf('rp-fe-reclaim', 'ReclaimableCharges', '006-wallet.png'),
    leaf('rp-fe-retro', 'Retrocession', '050-line-chart.png'),
    leaf('rp-cmp-mon', 'ComplianceDashboard', '097-analytics.png'),
    leaf('rp-cmp-rules', 'ComplianceRulesDashboard', '098-shield-1.png'),
    leaf('rp-cm-onb', 'OnboardingDashboard', '097-analytics.png'),
    leaf('rp-cm-pipe', 'OnboardingPipeline', '081-bar-chart.png'),
    leaf('rp-cm-aging', 'OnboardingAging', '057-wall-clock.png'),
    leaf('rp-cm-screen', 'ScreeningOutcomes', '099-shield.png'),
    leaf('rp-cm-docs', 'DocumentsStatus', '018-clipboard.png'),
  ]),

  group('my-workspaces', 'My Workspaces', '024-analytics-2.png', [
    leaf('mw-pl-inst', 'PLAnalytics', '024-analytics-2.png'),
    leaf('mw-pl-ac', 'PLAnalyticsRollupSummary', '024-analytics-2.png'),
    leaf('mw-events', 'UpcomingEvents', '057-wall-clock.png'),
  ]),

  group('user-dashboards', 'User Dashboards', '024-analytics-2.png', [
    leaf('ud-list', 'UserDashboardList', '062-magnifying-glass.png'),
    leaf('ud-designer', 'DashboardDesigner', '024-analytics-2.png'),
    leaf('ud-configurable', 'ConfigurableReporting', '024-analytics-2.png'),
  ]),

  group('import', 'Import', '001-archive.png', [
    leaf('im-heritage', 'ImportHeritage', 'Heritage.png'),
    leaf('im-jb', 'ImportJuliusBaer', 'JuliusBaer.png'),
    leaf('im-jb-recon', 'ImportJuliusBaerReconciliation', 'JuliusBaer.png'),
    leaf('im-citi', 'ImportCitibank', 'Citibank.png'),
    leaf('im-gs', 'ImportGoldmanSachs', 'GoldmanSachs.png'),
    leaf('im-jpm', 'ImportJPMorgan', 'JPMorgan.png'),
    leaf('im-pershing', 'ImportPershing', 'ValleyBank.png'),
    leaf('im-sg', 'ImportSocieteGenerale', 'SocieteGenerale.png'),
    leaf('im-safra', 'ImportSafra', 'Safra.png'),
    leaf('im-degroof', 'ImportDegroofPetercam', 'DegroofPetercam.png'),
    leaf('im-bbg', 'ImportBloomberg', 'Bloomberg.png'),
    leaf('im-mdp', 'ImportMarketDataPrices', '066-bar-chart-1.png'),
    leaf('im-fx', 'ImportForexRates', '055-euro.png'),
  ]),

  group('system', 'System', '089-gear.png', [
    shellLeaf('HomeHub', 'Home', '/home', '086-info.png', (p) => p === '/home'),
    shellLeaf('HomeHubAdvanced', 'Home (Advanced)', '/p/HomeHubAdvanced', '097-analytics.png'),
    shellLeaf('CommandExplorer', 'Command Explorer', '/p/CommandExplorer', '062-magnifying-glass.png'),
    leaf('sys-cmd-browser', 'ImportCommands', '018-clipboard.png'),
    leaf('sys-signalr', 'SignalRLogs', '034-newspaper.png'),
    leaf('sys-tt', 'TimeTravel', '057-wall-clock.png'),
    leaf('sys-ai', 'AiActivityConsole', '086-info.png'),
    leaf('sys-chist', 'CommandHistory', '023-contract-2.png'),
    leaf('sys-nhist', 'NotificationHistory', '014-bell.png'),
    leaf('sys-ent', 'EntitlementsAdmin', '089-gear.png'),
    leaf('sys-users', 'TenantUserAdmin', '046-group-1.png'),
    leaf('sys-seed', 'DataSeeding', '086-info.png'),
    leaf('sys-rep-struct', 'ReportingStructureEditor', '093-hierarchical-structure.png'),
    leaf('sys-user', 'UserSettings', '089-gear.png'),
    leaf('sys-login', 'Login', '049-user-1.png'),
    leaf('sys-reg', 'Register', '046-group-1.png'),
  ]),
];
